import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Divider, Grid } from "@mui/material";
import { useSelector } from "react-redux";

import Card from "../../../Card";
import EventLocation from "../../../EventLocation";
import TimelineSection from "../TimelineSection";
import EventClientSection from "./EventClientSection";
import TruncatedWithTooltip from "../../../TruncatedWithTooltip";
import { UIIcon } from "../../../ui";
import messages from "../../../../assets/locale/messages";
import { formatEventPaymentPeriod } from "../../../../utils/Helpers";
import { eventCheckinVerificationMethodsLocalization } from "../../../../utils/Constants";
import { mapEventProjectManagersToFormValues } from "../../../../utils/companyPortal/eventApi";

import "../_EventDetailFields.scss";

const Field = ({ label, value, xs = 6, sm = 3 }) => (
  <Grid item xs={xs} sm={sm}>
    <div className="event-detail-field">
      <span className="event-detail-label">{label}</span>
      <span className="event-detail-value">{value || "—"}</span>
    </div>
  </Grid>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  xs: PropTypes.number,
  sm: PropTypes.number,
};

Field.defaultProps = {
  value: "",
  xs: 6,
  sm: 3,
};

const iconColor = "var(--color-neutral-500)";

const EventBasicInfo = ({ event, portalVariant = "company-portal" }) => {
  const lang = useSelector((state) => state.locale.lang);
  const { events } = messages[lang];
  const t = events.viewEvents;
  const c = events.creatEvent.basicInformation;
  const showClientSection = portalVariant !== "client-portal";
  const showProjectManagers = portalVariant !== "client-portal";
  const isMonthly = event?.payment_period === "monthly";

  const timelineLabels = {
    start: c.start,
    end: c.end,
    notProvided: t.notProvided,
  };
  const verificationMethodKey =
    eventCheckinVerificationMethodsLocalization[
      event?.attendance_verification_method
    ];
  const verificationMethodLabel = verificationMethodKey
    ? c[verificationMethodKey]
    : "";
  const projectManagers = useMemo(
    () =>
      showProjectManagers
        ? mapEventProjectManagersToFormValues(event ?? {})
        : [],
    [event, showProjectManagers],
  );

  return (
    <Grid container spacing={3} alignItems="flex-start">
      <Grid item xs={12} md={8}>
        <Card title={t.basicInfo}>
          <Grid container spacing={3}>
            <Field
              label={t.eventType}
              value={
                lang === "ar"
                  ? event?.event_type?.name_ar
                  : event?.event_type?.name_en || event?.event_type?.name
              }
            />
            <Field
              label={t.recurrency}
              value={formatEventPaymentPeriod(
                event?.payment_period,
                events.listEvents,
              )}
            />
            {isMonthly && (
              <Field
                label={c.numberOfDays}
                value={String(event?.number_of_days ?? "")}
              />
            )}
            <Field
              label={t.paidDaysOff}
              value={String(event?.paid_days_off ?? "")}
            />
            <Field
              label={c.eventBrief}
              value={event?.description}
              xs={12}
              sm={12}
            />
          </Grid>

          <Divider />

          <Grid container spacing={3}>
            <TimelineSection
              title={c.actualEvent}
              icon={<UIIcon name="checkCalendar" size="sm" color={iconColor} />}
              start={event?.event_start_at}
              end={event?.event_end_at}
              tLabels={timelineLabels}
              gridItemProps={{ xs: 12, sm: 8, md: 7 }}
            />
          </Grid>
        </Card>

        <Box sx={{ mt: 3 }}>
          <Card title={c.managementVerification}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                <span className="event-detail-label">{c.checkInMethod}</span>
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    alignSelf: "flex-start",
                    width: "fit-content",
                    height: 26,
                    border: "1px solid var(--color-background-subtle, #FAFAFA)",
                    borderRadius: "8px",
                    bgcolor: "var(--color-background-subtle, #FAFAFA)",
                    color: "var(--color-sidebar-gradient-start, #31237D)",
                    fontSize: "12px",
                    fontWeight: 500,
                    lineHeight: "16px",
                    px: "12px",
                    py: "4px",
                  }}
                >
                  {verificationMethodLabel || "—"}
                </Box>
              </Box>

              {showProjectManagers ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                <span className="event-detail-label">
                  {events.creatEvent.projectManager}
                </span>
                {projectManagers.length ? (
                  <div className="event-detail-pm-list">
                    {projectManagers.map((manager) => (
                      <div
                        className="event-detail-pm-card"
                        key={
                          manager.company_user_id ??
                          manager.assignmentId ??
                          manager.name
                        }
                      >
                        <div className="event-detail-pm-card__body">
                          <TruncatedWithTooltip title={manager.name}>
                            <strong>{manager.name}</strong>
                          </TruncatedWithTooltip>
                          <TruncatedWithTooltip
                            title={manager.role || manager.responsibility}
                          >
                            <span>{manager.role || manager.responsibility}</span>
                          </TruncatedWithTooltip>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="event-detail-value event-detail-value--muted">
                    {t.notProvided}
                  </span>
                )}
              </Box>
              ) : null}
            </Box>
          </Card>
        </Box>

        {showClientSection ? (
          <Box sx={{ mt: 3 }}>
            <EventClientSection
              event={event}
              labels={{
                client: t.client,
                clientName: c.clientName,
                clientEmail: t.clientEmail,
                clientPhone: t.clientPhone,
                noClientAssigned: t.noClientAssigned,
                noClientAssignedDescription: t.noClientAssignedDescription,
              }}
            />
          </Box>
        ) : null}
      </Grid>

      <Grid item xs={12} md={4}>
        <EventLocation
          lat={event?.lat}
          lng={event?.lng}
          address={event?.address}
          radius={event?.event_radius}
          city={event?.city}
        />
      </Grid>
    </Grid>
  );
};

EventBasicInfo.propTypes = {
  event: PropTypes.object,
  portalVariant: PropTypes.oneOf(["company-portal", "client-portal"]),
};

EventBasicInfo.defaultProps = {
  event: null,
  portalVariant: "company-portal",
};

export default EventBasicInfo;
